import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Grid, Typography, Divider } from '@material-ui/core';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import { LOCATIONS_CHANNEL_PAGE } from '@/constants/locations';
import { CHANNEL_PAGE } from '@/constants/swimlanes';
import {
  ROUTE_404,
  ROUTE_DISCOVER_CHANNEL,
  ROUTE_PARTNER_DISCOVER_CHANNEL,
} from '@/constants/routes';

// UTILITY
import { analyticsPageLoaded } from '@/utility/analytics';
import { sendParentMessage } from '@/utility/common';
import { getLink, routeEnhance } from '@/utility/routes';
import store from '@/utility/store';

// STORE
import { AppDetailsActions, ChannelActions, SwimlanesActions } from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import Swimlanes from '@/components/Swimlanes';
import Loader from '@/components/Loader';
import Zipcode from '@/components/Zipcode';

// STYLES
import { Container } from './styles';
import { getOrgSchema } from '@/utility/orgSchemas/orgSchemas';

class ChannelDetails extends PureComponent {
  componentDidMount() {
    window.prerendercloudReady = false;

    this.getPageContent();
  }

  componentDidUpdate(prevProps) {
    const {
      router: {
        params: { slug },
      },
      isIframe,
      isAuth,
    } = this.props;

    if (prevProps.router.params.slug !== slug || prevProps.isAuth !== isAuth) {
      this.getPageContent();
      window.scrollTo(0, 0);

      if (isIframe) {
        sendParentMessage({ goToTop: true });
      }
    }
  }

  componentWillUnmount() {
    const { swimlanesClear } = this.props;

    swimlanesClear();
  }

  getPageContent = () => {
    const {
      router: {
        params: { slug },
        navigate,
      },
      getChannelDetails,
    } = this.props;

    getChannelDetails(slug, () => {
      const enhancedRoute = routeEnhance(ROUTE_404);

      navigate(enhancedRoute, { replace: true, state: enhancedRoute.state });
    });
  };

  handleUpdateAppStatus = (data, status) => {
    const { appListUpdateAppStatus } = this.props;

    appListUpdateAppStatus(data.id, status);
  };

  handleZipcodeSumbit = values => {
    const { data, getChannelSwimlanes } = this.props;

    const params = {
      zip: values.zipcode,
      dma_id: values.dma_id,
      channel_id: data.channel_id,
    };

    getChannelSwimlanes(params);
  };

  handleChangeHelmetData = () => {
    if (this.lastPath !== window.location.pathname) {
      analyticsPageLoaded();
      this.lastPath = window.location.pathname;

      window.prerendercloudReady = true;
    }
  };

  render() {
    const {
      t,
      router: {
        params: { slug },
      },
      data,
      loading,
      section1,
      section1Loadings,
      partner,
      partnerLoading,
    } = this.props;

    const isPartner = !!partner.slug;
    const dma_id = store.get('dma_id');

    const seoData = {
      ...data,
      canonicalUrl: `${siteUrl}${getLink(ROUTE_DISCOVER_CHANNEL, { slug })}`,
      ogUrl: isPartner
        ? `${siteUrl}${getLink(ROUTE_PARTNER_DISCOVER_CHANNEL, { partner: partner.slug, slug })}`
        : `${siteUrl}${getLink(ROUTE_DISCOVER_CHANNEL, { slug })}`,
      title: data.name,
      description: `MyBundle - ${data.description}`,
      image: data.img,
    };

    return (
      <Layout>
        {!loading && data.name && !partnerLoading && (
          <Helmet
            onChangeClientState={this.handleChangeHelmetData}
            script={[getOrgSchema('channel', seoData)]}
            titleTemplate="%s | MyBundle"
          >
            <title>{seoData.title}</title>
            <meta property="og:title" content={seoData.title} />
            <meta property="og:site_name" content="MyBundle" />
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            <meta name="description" content={seoData.description} />
            <meta property="og:description" content={seoData.description} />
          </Helmet>
        )}
        <Container className="channel-page">
          <div className="channel-page__hero hero section section--grey">
            {loading ? (
              <Loader active fullFilled />
            ) : (
              <div className="page-content">
                <Grid container spacing={4}>
                  <Grid item xs={12} sm={5} md={4}>
                    {!!data.img && (
                      <div className="hero__logo">
                        <img src={data.img} alt={data.name} />
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={7} md={8}>
                    {!!data.name && (
                      <>
                        <Typography className="m-b-20" variant="h3" component="h1">
                          {data.name}
                        </Typography>
                        <Divider className="m-b-15" />
                      </>
                    )}
                    {!!data.description && (
                      <Typography className="hero__description">{data.description}</Typography>
                    )}
                    {data.show_form && !dma_id && (
                      <div className="hero__zipcode zipcode">
                        <Zipcode
                          label={t('channelDetails.zipTitle')}
                          onSubmit={this.handleZipcodeSumbit}
                          analyticsLocation={LOCATIONS_CHANNEL_PAGE}
                        />
                      </div>
                    )}
                  </Grid>
                </Grid>
              </div>
            )}
          </div>
          <div className="channel-page__swimlanes section">
            <div className="page-content">
              <Swimlanes
                className="channel-page__collection"
                data={section1}
                loadings={section1Loadings}
                loadingRows={2}
                analyticsLocation={LOCATIONS_CHANNEL_PAGE}
                handleUpdateAppStatus={this.handleUpdateAppStatus}
              />
            </div>
          </div>
        </Container>
      </Layout>
    );
  }
}

ChannelDetails.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  isIframe: PropTypes.bool.isRequired,
  isAuth: PropTypes.bool.isRequired,
  data: PropTypes.object.isRequired,
  getChannelDetails: PropTypes.func.isRequired,
  getChannelSwimlanes: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  section1: PropTypes.object,
  section1Loadings: PropTypes.object.isRequired,
  appListUpdateAppStatus: PropTypes.func.isRequired,
  swimlanesClear: PropTypes.func.isRequired,
  partner: PropTypes.object.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ app, auth, channel, swimlanes, partner }) => ({
  isIframe: app.isIframe,
  isAuth: auth.isAuth,
  data: channel.data,
  loading: channel.loading,
  section1: swimlanes.section1[CHANNEL_PAGE] || {},
  section2: swimlanes.section2[CHANNEL_PAGE] || {},
  section1Loadings: swimlanes.loadings.section1[CHANNEL_PAGE] || {},
  section2Loadings: swimlanes.loadings.section2[CHANNEL_PAGE] || {},
  partner: partner.data,
  partnerLoading: partner.loadings.partner,
});

const mapDispatchToProps = dispatch => ({
  getChannelDetails: (slug, onError) => {
    dispatch(ChannelActions.getChannelDetails.action({ slug, onError }));
  },
  getChannelSwimlanes: params => {
    dispatch(ChannelActions.getChannelSwimlanes.action({ params }));
  },
  appListUpdateAppStatus: (id, status) => {
    dispatch(AppDetailsActions.appListUpdateAppStatus.action({ id, status }));
  },
  swimlanesClear: () => dispatch(SwimlanesActions.swimlanesClear.action()),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(),
  withRouter,
)(ChannelDetails);
